// BL-2586 — the contact person's three fields, the ONE Zod shape both contact
// editors read (ADR 016 / rule #27): the auction's own Contact page
// (`auction-settings/contact.ts`) and the house default on Settings › Auctions ›
// Defaults (`house-defaults.ts`, BL-2782). One shape, so a bound the
// house default accepts can never be one an auction's page rejects.

import { z } from '@auctogon/core/zod';
import { stripNulCharacters } from '@auctogon/core/validation/input-guards';
import { toStoredContactPhone } from '@auctogon/core/validation/phone';

import { addPhoneFormatIssue } from './phone-field';

/** BL-2478: the longest email any contact field stores (RFC 5321's path limit). */
export const CONTACT_EMAIL_MAX = 254;

const blankToNull = (value: string): string | null => (value === '' ? null : value);

/**
 * BL-2478 — one contact text field: a NUL is stripped (jsonb and text columns refuse it, so it
 * used to 500), the value is trimmed and bounded, and a blank one is stored as `null`, never "".
 *
 * @param max - the longest value stored.
 * @param tooLong - the message when it is longer.
 * @returns the nullable field schema.
 */
export function contactText(max: number, tooLong: string) {
  return z.string().overwrite(stripNulCharacters).trim().max(max, tooLong).transform(blankToNull).nullable();
}

/**
 * BL-2478 — one contact email: as {@link contactText}, then a real address or nothing. A
 * whitespace-only email clears the field like any other blank one (it used to be refused).
 *
 * @returns the nullable email schema.
 */
export function contactEmail() {
  // Piped, not refined, so a bad address keeps Zod's own `invalid_format` code on the wire.
  return z
    .string()
    .overwrite(stripNulCharacters)
    .trim()
    .pipe(z.union([z.literal(''), z.string().max(CONTACT_EMAIL_MAX, 'Email is too long.').email('Enter a valid email address.')]))
    .transform(blankToNull)
    .nullable();
}

/**
 * The three contact fields as a plain object shape, for a schema that carries a contact beside
 * other fields (BL-2782's house defaults). Pair it with {@link refineContactPhone}.
 */
export const ContactPersonShape = z.object({
  contactName: contactText(200, 'Contact name is too long.'),
  contactPhone: contactText(50, 'Contact phone is too long.').transform(toStoredContactPhone),
  contactEmail: contactEmail(),
});

/**
 * Name, phone, email — every field nullable free text. Plain `z.object()`:
 * unknown keys are STRIPPED, never rejected. BL-2478: the phone is stored as E.164 when it is a
 * real number (as a lot contact row's is). BUG-2132 AC 6: a changed phone that is not a real
 * number is refused with the shared "Enter a valid phone number." (BL-1277's rule), the same
 * outcome a typed lot contact phone gets; the phone already stored still saves untouched.
 *
 * @param options.persistedPhone - The stored contact phone, read server-side from the auction or
 *   tenant row (the page passes the value it loaded, as an advisory copy).
 * @returns The contact-person schema.
 */
export function makeContactPersonSchema(options: { persistedPhone?: string | null } = {}) {
  return ContactPersonShape.superRefine(refineContactPhone(options));
}

/**
 * BUG-2132 AC 6's phone rule as a `superRefine` callback, shared by every schema built on
 * {@link ContactPersonShape}: a changed phone must be a real number; the stored one still saves.
 *
 * @param options.persistedPhone - The stored contact phone (see {@link makeContactPersonSchema}).
 * @returns The refinement.
 */
export function refineContactPhone(options: { persistedPhone?: string | null } = {}) {
  return (values: { contactPhone: string | null }, ctx: z.RefinementCtx): void =>
    addPhoneFormatIssue(values.contactPhone, ctx, { path: ['contactPhone'], persistedValue: options.persistedPhone });
}

/** The contact-person schema with no stored phone to grandfather against. */
export const ContactPersonSchema = makeContactPersonSchema();

/** The three contact-person values, as a form holds them and a PUT sends them. */
export type ContactPersonValues = z.infer<typeof ContactPersonShape>;
